import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Plus, Pencil, Trash2, TextSearch, X, Check, Webhook } from "lucide-react";
import {
  getKeywordSearches,
  createKeywordSearch,
  updateKeywordSearch,
  deleteKeywordSearch,
  type KeywordSearch,
  type KeywordSearchInput,
} from "../../services/KeywordSearchService";
import { getDiscordWebhooks } from "../../services/DiscordWebhookService";

const FREQUENCY_OPTIONS = [1, 2, 4, 6, 12, 24];

// Display names for the online retailers offered by the sidebar retailer selector.
const RETAILER_LABELS: Record<string, string> = {
  amazon: "Amazon",
  walmart: "Walmart",
  lowes: "Lowe's",
  academy: "Academy Sports",
  adidas: "Adidas",
  belk: "Belk",
  bestbuy: "Best Buy",
  bjs: "BJ's Wholesale",
  columbia: "Columbia",
  costco: "Costco",
  home_depot: "Home Depot",
  jcpenney: "JCPenney",
  kohls: "Kohl's",
  meijer: "Meijer",
  menards: "Menards",
  michaels: "Michaels",
  newegg: "Newegg",
  nike: "Nike",
  nordstromrack: "Nordstrom Rack",
  puma: "Puma",
  reebok: "Reebok",
  skechers: "Skechers",
  target: "Target",
  underarmour: "Under Armour",
  walgreens: "Walgreens",
  woot: "Woot",
  zappos: "Zappos",
};

const retailerLabel = (id: string) => RETAILER_LABELS[id] ?? id;

const emptyForm = (): KeywordSearchInput => ({ keyword: "", retailers: [], frequencyHours: 1, enabled: true, notifyDiscordWebhookId: "" });

type FormMode = { type: "closed" } | { type: "create" } | { type: "edit"; search: KeywordSearch };

function formatFrequency(hours: number): string {
  return hours === 1 ? "Every hour" : `Every ${hours}h`;
}

function formatLastRun(lastRunAt?: number): string {
  if (!lastRunAt) return "Never";
  const mins = Math.floor((Date.now() - lastRunAt) / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 48) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

export default function KeywordSearchesTab() {
  const queryClient = useQueryClient();
  const [formMode, setFormMode] = useState<FormMode>({ type: "closed" });
  const [form, setForm] = useState<KeywordSearchInput>(emptyForm());
  const [formError, setFormError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["admin-keyword-searches"],
    queryFn: getKeywordSearches,
  });
  const { data: webhookData } = useQuery({
    queryKey: ["admin-discord-webhooks"],
    queryFn: getDiscordWebhooks,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["admin-keyword-searches"] });
  const closeForm = () => { setFormMode({ type: "closed" }); setForm(emptyForm()); setFormError(null); };

  const createMutation = useMutation({
    mutationFn: createKeywordSearch,
    onSuccess: () => { invalidate(); closeForm(); },
    onError: (err: Error) => setFormError(err.message || "Failed to create keyword search"),
  });
  const updateMutation = useMutation({
    mutationFn: ({ id, input }: { id: string; input: KeywordSearchInput }) => updateKeywordSearch(id, input),
    onSuccess: () => { invalidate(); closeForm(); },
    onError: (err: Error) => setFormError(err.message || "Failed to update keyword search"),
  });
  const toggleMutation = useMutation({
    mutationFn: (s: KeywordSearch) =>
      updateKeywordSearch(s.id, { keyword: s.keyword, retailers: s.retailers, frequencyHours: s.frequencyHours, enabled: !s.enabled, notifyDiscordWebhookId: s.notifyDiscordWebhookId ?? "" }),
    onSuccess: invalidate,
  });
  const deleteMutation = useMutation({
    mutationFn: deleteKeywordSearch,
    onSuccess: invalidate,
  });

  const isSubmitting = createMutation.isPending || updateMutation.isPending;
  const searches = data?.searches ?? [];
  const webhooks = webhookData?.webhooks ?? [];
  const webhookName = (id?: string) => webhooks.find((w) => w.id === id)?.name;
  const implemented = new Set(data?.implementedRetailers ?? []);
  const allRetailers = data?.supportedRetailers?.length ? data.supportedRetailers : Object.keys(RETAILER_LABELS);
  // Searchable-today retailers first, then the rest, alphabetical within each group.
  const retailerOptions = [...allRetailers].sort((a, b) => {
    const ai = implemented.has(a) ? 0 : 1;
    const bi = implemented.has(b) ? 0 : 1;
    return ai !== bi ? ai - bi : retailerLabel(a).localeCompare(retailerLabel(b));
  });
  const hasPendingSelectable = retailerOptions.some((r) => !implemented.has(r));

  const openCreate = () => { setForm(emptyForm()); setFormError(null); setFormMode({ type: "create" }); };
  const openEdit = (s: KeywordSearch) => {
    setForm({ keyword: s.keyword, retailers: [...s.retailers], frequencyHours: s.frequencyHours, enabled: s.enabled, notifyDiscordWebhookId: s.notifyDiscordWebhookId ?? "" });
    setFormError(null);
    setFormMode({ type: "edit", search: s });
  };

  const toggleRetailer = (id: string) =>
    setForm((f) => ({
      ...f,
      retailers: f.retailers.includes(id) ? f.retailers.filter((r) => r !== id) : [...f.retailers, id],
    }));

  const submit = () => {
    if (!form.keyword.trim()) { setFormError("Keyword is required"); return; }
    if (form.retailers.length === 0) { setFormError("Select at least one retailer"); return; }
    const input = { ...form, keyword: form.keyword.trim() };
    if (formMode.type === "create") createMutation.mutate(input);
    else if (formMode.type === "edit") updateMutation.mutate({ id: formMode.search.id, input });
  };

  const handleDelete = (s: KeywordSearch) => {
    if (confirm(`Delete keyword search "${s.keyword}"?`)) deleteMutation.mutate(s.id);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-bold text-gray-900 dark:text-white">Keyword searches</h2>
          <p className="text-xs text-gray-400 dark:text-dark-400 mt-0.5">
            Searched hourly against online retailers by the ultima-keywords runner; each entry runs at its own frequency.
          </p>
        </div>
        <button
          onClick={openCreate}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-900 dark:bg-white text-white dark:text-dark-900 hover:opacity-90"
        >
          <Plus className="w-3.5 h-3.5" /> Add keyword
        </button>
      </div>

      {formMode.type !== "closed" && (
        <div className="bg-white dark:bg-dark-800 rounded-2xl ring-1 ring-gray-200 dark:ring-dark-700 shadow-sm p-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-[13px] font-semibold text-gray-900 dark:text-white">
              {formMode.type === "create" ? "New keyword search" : `Edit "${formMode.search.keyword}"`}
            </p>
            <button onClick={closeForm} className="p-1 text-gray-400 hover:text-gray-900 dark:hover:text-white" aria-label="Close form">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-12 gap-3 mb-3">
            <div className="sm:col-span-7">
              <label className="block text-[10px] font-bold uppercase tracking-wide text-gray-400 mb-1">Keyword</label>
              <input
                className="w-full px-2.5 py-1.5 text-sm rounded-lg border border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                value={form.keyword}
                placeholder="e.g. dewalt drill"
                onChange={(e) => setForm((f) => ({ ...f, keyword: e.target.value }))}
              />
            </div>
            <div className="sm:col-span-3">
              <label className="block text-[10px] font-bold uppercase tracking-wide text-gray-400 mb-1">Frequency</label>
              <select
                className="w-full px-2.5 py-1.5 text-sm rounded-lg border border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-700 text-gray-900 dark:text-white"
                value={form.frequencyHours}
                onChange={(e) => setForm((f) => ({ ...f, frequencyHours: Number(e.target.value) }))}
              >
                {FREQUENCY_OPTIONS.map((h) => (
                  <option key={h} value={h}>{formatFrequency(h)}</option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-2 flex items-end">
              <label className="flex items-center gap-1.5 text-xs font-medium text-gray-700 dark:text-dark-200 pb-2">
                <input
                  type="checkbox"
                  checked={form.enabled}
                  onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))}
                  className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                />
                Enabled
              </label>
            </div>
          </div>

          <div>
            <div className="flex items-baseline justify-between mb-1.5">
              <label className="text-[10px] font-bold uppercase tracking-wide text-gray-400">
                Retailers <span className="normal-case font-medium">— select one or more</span>
              </label>
              {form.retailers.length > 0 && (
                <span className="text-[11px] text-gray-400 dark:text-dark-400">{form.retailers.length} selected</span>
              )}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {retailerOptions.map((r) => {
                const selected = form.retailers.includes(r);
                const pending = !implemented.has(r);
                return (
                  <button
                    key={r}
                    type="button"
                    onClick={() => toggleRetailer(r)}
                    aria-pressed={selected}
                    className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold ring-1 transition-colors ${selected
                      ? "bg-gray-900 dark:bg-white text-white dark:text-dark-900 ring-gray-900 dark:ring-white"
                      : "bg-white dark:bg-dark-700 text-gray-600 dark:text-dark-200 ring-gray-200 dark:ring-dark-600 hover:ring-gray-400 dark:hover:ring-dark-400"
                      }`}
                  >
                    {selected && <Check className="w-3 h-3" />}
                    {retailerLabel(r)}
                    {pending && <span className="opacity-60">*</span>}
                  </button>
                );
              })}
            </div>
            {hasPendingSelectable && (
              <p className="mt-1.5 text-[11px] text-gray-400 dark:text-dark-400">
                * keyword search not wired up for this retailer yet — selections are saved and start running once it is.
              </p>
            )}
          </div>

          <div className="mt-3">
            <label className="block text-[10px] font-bold uppercase tracking-wide text-gray-400 mb-1">
              Notify Discord <span className="normal-case font-medium">— optional</span>
            </label>
            <div className="sm:max-w-xs">
              <select
                className="w-full px-2.5 py-1.5 text-sm rounded-lg border border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-700 text-gray-900 dark:text-white"
                value={form.notifyDiscordWebhookId ?? ""}
                onChange={(e) => setForm((f) => ({ ...f, notifyDiscordWebhookId: e.target.value }))}
              >
                <option value="">No notification</option>
                {webhooks.map((w) => (
                  <option key={w.id} value={w.id}>{w.name}</option>
                ))}
              </select>
            </div>
            <p className="mt-1.5 text-[11px] text-gray-400 dark:text-dark-400">
              {webhooks.length === 0
                ? "No webhooks configured yet — add one in the Discord webhooks tab to enable notifications."
                : "Pings the selected webhook when a found product is at least 70% off."}
            </p>
          </div>

          {formError && <p className="mt-2 text-xs text-red-600 dark:text-red-400">{formError}</p>}
          <div className="mt-3 flex gap-2">
            <button
              onClick={submit}
              disabled={isSubmitting}
              className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-semibold bg-gray-900 dark:bg-white text-white dark:text-dark-900 hover:opacity-90 disabled:opacity-40"
            >
              {isSubmitting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              {formMode.type === "create" ? "Create" : "Save changes"}
            </button>
            <button
              onClick={closeForm}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold text-gray-600 dark:text-dark-300 hover:bg-gray-100 dark:hover:bg-dark-700"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {searches.length === 0 ? (
        <div className="bg-white dark:bg-dark-800 rounded-2xl ring-1 ring-gray-200 dark:ring-dark-700 shadow-sm py-14 flex flex-col items-center text-center">
          <TextSearch className="w-8 h-8 text-gray-300 dark:text-dark-500 mb-2" />
          <p className="text-sm font-medium text-gray-900 dark:text-white">No keyword searches yet</p>
          <p className="text-xs text-gray-400 dark:text-dark-400 mt-1">Add a keyword to start scanning retailers on a schedule.</p>
        </div>
      ) : (
        <div className="bg-white dark:bg-dark-800 rounded-2xl ring-1 ring-gray-200 dark:ring-dark-700 shadow-sm overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-gray-100 dark:border-dark-700">
                <th className="px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.14em] text-gray-400 dark:text-dark-400">Keyword</th>
                <th className="px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.14em] text-gray-400 dark:text-dark-400">Retailers</th>
                <th className="px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.14em] text-gray-400 dark:text-dark-400">Frequency</th>
                <th className="px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.14em] text-gray-400 dark:text-dark-400">Notify</th>
                <th className="px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.14em] text-gray-400 dark:text-dark-400">Last run</th>
                <th className="px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.14em] text-gray-400 dark:text-dark-400">Status</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {searches.map((s) => (
                <tr key={s.id} className="border-b border-gray-50 dark:border-dark-700/60 last:border-0 align-top">
                  <td className="px-4 py-3 text-[13px] font-medium text-gray-900 dark:text-white whitespace-nowrap">{s.keyword}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1 max-w-md">
                      {s.retailers.map((r) => {
                        const pending = !implemented.has(r);
                        return (
                          <span
                            key={r}
                            title={pending ? "Keyword search not wired up for this retailer yet" : undefined}
                            className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold ${pending
                              ? "bg-gray-50 dark:bg-dark-700/50 text-gray-400 dark:text-dark-400 ring-1 ring-dashed ring-gray-200 dark:ring-dark-600"
                              : "bg-gray-100 dark:bg-dark-700 text-gray-700 dark:text-dark-200"
                              }`}
                          >
                            {retailerLabel(r)}
                            {pending && <span className="opacity-60">*</span>}
                          </span>
                        );
                      })}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-[13px] text-gray-600 dark:text-dark-300 whitespace-nowrap">{formatFrequency(s.frequencyHours)}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {s.notifyDiscordWebhookId ? (
                      <span
                        className="inline-flex items-center gap-1 text-[12px] text-gray-600 dark:text-dark-300"
                        title="Pings this Discord webhook for products ≥70% off"
                      >
                        <Webhook className="w-3 h-3 text-gray-400 dark:text-dark-400" />
                        {webhookName(s.notifyDiscordWebhookId) ?? <span className="text-amber-600 dark:text-amber-400">{s.notifyDiscordWebhookId} (missing)</span>}
                      </span>
                    ) : (
                      <span className="text-[13px] text-gray-300 dark:text-dark-500">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-[13px] text-gray-600 dark:text-dark-300 whitespace-nowrap">{formatLastRun(s.lastRunAt)}</td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => toggleMutation.mutate(s)}
                      disabled={toggleMutation.isPending}
                      className={`px-2 py-0.5 rounded-full text-[11px] font-semibold transition-colors ${s.enabled
                        ? "bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400 hover:bg-green-200 dark:hover:bg-green-900/60"
                        : "bg-gray-100 dark:bg-dark-700 text-gray-500 dark:text-dark-400 hover:bg-gray-200 dark:hover:bg-dark-600"
                        }`}
                      title="Toggle enabled"
                    >
                      {s.enabled ? "Enabled" : "Paused"}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => openEdit(s)}
                        className="p-1.5 rounded-lg text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-dark-700"
                        aria-label={`Edit ${s.keyword}`}
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => handleDelete(s)}
                        disabled={deleteMutation.isPending}
                        className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40"
                        aria-label={`Delete ${s.keyword}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
